// NOTE: We dispatch UI toast via global CustomEvent to avoid duplicate toasts.

/**
 * Global navigation function that can be set from the app root
 * This allows us to navigate without using hooks in non-component contexts
 */
let globalNavigateFunction: ((path: string) => void) | null = null;

export const setGlobalNavigate = (navigateFn: (path: string) => void) => {
  globalNavigateFunction = navigateFn;
};

/**
 * Global error handler that doesn't use React hooks
 * Can be used in RTK Query baseQuery, services, and utilities
 */
export const handleGlobalError = async (error: any) => {
  const showErrorToast = (message: string, variant: 'error' | 'warning' | 'info' = 'error') => {
    const severityConfig = {
      error: { variant: 'destructive' as const, title: 'Error' },
      warning: { variant: 'default' as const, title: 'Warning' },
      info: { variant: 'default' as const, title: 'Information' },
    } as const;
    const config = severityConfig[variant];
    try {
      const event = new CustomEvent('showErrorToast', {
        detail: {
          title: config.title,
          description: message,
          variant: config.variant,
          status: 0,
          tagName: 'GlobalErrorHandler',
        },
      });
      window.dispatchEvent(event);
    } catch (e) {
      // Fallback for environments without DOM
      if (import.meta.env?.DEV) console.error('Failed to dispatch showErrorToast event', e);
    }
  };

  const err =
    error.response?.data?.message ||
    error.data?.message ||
    error.message ||
    'An unexpected error occurred';

  // Handle blob responses (file downloads)
  if (error?.config?.responseType === 'blob') {
    try {
      const errorText = await error?.response?.data.text();
      const errorObj = JSON.parse(errorText);
      showErrorToast(errorObj.message || 'File download failed', 'error');
      return;
    } catch (parseError) {
      showErrorToast('An error occurred while processing the file', 'error');
      return;
    }
  }

  // Handle different HTTP status codes
  switch (error.response?.status || error.status) {
    case 401:
      showErrorToast(err || 'Token has expired, Please login again!', 'error');

      // Clear session storage
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('authToken');
      sessionStorage.clear();

      // Navigate to login using global navigator
      setTimeout(() => {
        if (globalNavigateFunction) {
          globalNavigateFunction('/login');
        } else {
          // Fallback to window.location if global navigator not available
          window.location.href = '/login';
        }
      }, 2000);
      break;

    case 403:
      showErrorToast(err || 'Token has expired, Please login again!', 'error');

      // Clear session storage
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('authToken');
      sessionStorage.clear();

      // Navigate to login using global navigator
      setTimeout(() => {
        if (globalNavigateFunction) {
          globalNavigateFunction('/login');
        } else {
          // Fallback to window.location if global navigator not available
          window.location.href = '/login';
        }
      }, 2000);
      break;

    default:
      console.log('Error');
      break;
  }

  // Log error for debugging (only in development)
  if (import.meta.env.DEV) {
    console.error('Global Error Handler:', error);
  }
};

/**
 * Utility to check if an error should be handled globally
 * Some errors might need custom handling in specific components
 */
export const shouldHandleGlobally = (error: any): boolean => {
  // Add any conditions where you don't want global handling
  // For example, if error has a specific flag
  return !error?.skipGlobalHandling;
};
